import { all, call, put, select, takeEvery, takeLatest } from "redux-saga/effects";
import { SteeringDataAPI, Request } from "@ai360/core";
import { actions as notificationActions, MSGTYPE } from "~/notifications";
import { actions as messagingActions } from "~/messaging";
import { getTheUserCompanyGuid } from "~/login/selectors";

import * as actions from "./actions";
import { getLoadedChildParentGuids } from "./selectors";

const STEERING_DATA_STATUS_EVENT = "steeringDataStatus";
const STEERING_DATA_LIST_CHANGED_EVENT = "steeringDataListChanged";

// --- Fetch List ---

export function* fetchSteeringDataListSaga(action) {
    const silent = action?.payload?.silent === true;
    try {
        if (!silent) {
            yield put(actions.setSteeringDataLoading(true));
        }
        const ownerGuid = yield select(getTheUserCompanyGuid);
        const response = yield call(SteeringDataAPI.getSteeringDataList, ownerGuid);
        yield put(actions.fetchSteeringDataListSuccess(response));
    } catch (error) {
        yield put(
            actions.fetchSteeringDataListFailed(
                error?.message || "Failed to fetch steering data list"
            )
        );
        yield put(notificationActions.apiCallError(error, action));
    }
}

// --- Fetch Single Record ---

export function* fetchSteeringDataRecordSaga(action) {
    const { payload } = action;
    try {
        yield put(actions.setSteeringDataLoading(true));
        const response = yield call(SteeringDataAPI.getSteeringDataRecord, payload);
        yield put(actions.fetchSteeringDataRecordSuccess(response));
    } catch (error) {
        yield put(
            actions.fetchSteeringDataRecordFailed(
                error?.message || "Failed to fetch steering data record"
            )
        );
        yield put(notificationActions.apiCallError(error, action));
    }
}

// --- Create ---
// Handles file upload flow: create record → get pre-signed URL → upload file

export function* createSteeringDataSaga(action) {
    const { payload } = action;
    const { file, ...createRequest } = payload;
    try {
        yield put(actions.setSteeringDataLoading(true));

        // Ensure ownerGuid is included in the request body (required by the RESTful API)
        const ownerGuid = yield select(getTheUserCompanyGuid);
        const requestWithOwner = { ...createRequest, ownerGuid };

        // Step 1: Create the steering data record
        const record = yield call(SteeringDataAPI.createSteeringData, requestWithOwner);

        // Step 2: If a file was provided, upload it via pre-signed URL
        if (file) {
            // Get pre-signed URL from backend
            const presignedResponse = yield call(
                SteeringDataAPI.getUploadPresignedUrl,
                record.steeringDataGuid,
                file.name,
                ownerGuid
            );

            // Upload file directly to S3 using the pre-signed URL
            yield call(Request.uploadToS3, presignedResponse.url, file, "application/octet-stream");

            // Trigger backend file processing (text extraction + embedding)
            try {
                yield call(SteeringDataAPI.processFile, record.steeringDataGuid);
            } catch (processError) {
                // Non-fatal: file is uploaded, processing will occur on next deploy
                console.warn("process-file trigger failed (non-blocking):", processError);
            }
        }

        // Step 3: Dispatch success
        yield put(actions.createSteeringDataSuccess(record));
        yield put(
            notificationActions.pushToasterMessage(
                "Steering data record created successfully.",
                MSGTYPE.SUCCESS
            )
        );

        // Refresh the list
        yield put(actions.fetchSteeringDataList());
    } catch (error) {
        yield put(
            actions.createSteeringDataFailed(error?.message || "Failed to create steering data")
        );
        yield put(notificationActions.apiCallError(error, action));
    }
}

// --- Create Batch ---

export function* createSteeringDataBatchSaga(action) {
    const { payload } = action;
    const { files, baseName, ...baseRequest } = payload;

    try {
        yield put(actions.setSteeringDataLoading(true));

        const ownerGuid = yield select(getTheUserCompanyGuid);

        let succeeded = 0;
        const failedNames = [];

        let index = 1;
        for (const file of files) {
            try {
                const recordName = `${baseName} ${index}`;
                const requestWithOwner = {
                    ...baseRequest,
                    ownerGuid,
                    name: recordName,
                    sourceType: "file",
                };

                // Step 1: create record
                const record = yield call(SteeringDataAPI.createSteeringData, requestWithOwner);

                // Step 2: presigned URL + upload
                const presignedResponse = yield call(
                    SteeringDataAPI.getUploadPresignedUrl,
                    record.steeringDataGuid,
                    file.name,
                    ownerGuid
                );
                yield call(
                    Request.uploadToS3,
                    presignedResponse.url,
                    file,
                    "application/octet-stream"
                );

                // Step 3: trigger processing
                try {
                    yield call(SteeringDataAPI.processFile, record.steeringDataGuid);
                } catch (processError) {
                    console.warn("process-file trigger failed (non-blocking):", processError);
                }

                succeeded += 1;
            } catch (fileError) {
                console.warn(`Failed to create record for ${file.name}:`, fileError);
                failedNames.push(file.name);
            } finally {
                index += 1;
            }
        }

        yield put(actions.createSteeringDataBatchSuccess({ succeeded, failed: failedNames }));

        if (failedNames.length === 0) {
            yield put(
                notificationActions.pushToasterMessage(
                    `${succeeded} steering data record${
                        succeeded === 1 ? "" : "s"
                    } created successfully.`,
                    MSGTYPE.SUCCESS
                )
            );
        } else {
            yield put(
                notificationActions.pushToasterMessage(
                    `${succeeded} created, ${failedNames.length} failed: ${failedNames.join(", ")}`,
                    MSGTYPE.WARNING
                )
            );
        }

        // Refresh the list once
        yield put(actions.fetchSteeringDataList());
    } catch (error) {
        yield put(
            actions.createSteeringDataBatchFailed(
                error?.message || "Failed to create steering data records"
            )
        );
        yield put(notificationActions.apiCallError(error, action));
    }
}

// --- Update ---

export function* updateSteeringDataSaga(action) {
    const { payload } = action;
    const { steeringDataGuid, file, ...updateRequest } = payload;
    try {
        yield put(actions.setSteeringDataLoading(true));
        const ownerGuid = yield select(getTheUserCompanyGuid);

        // Step 1: Update the steering data record
        const record = yield call(
            SteeringDataAPI.updateSteeringData,
            steeringDataGuid,
            updateRequest
        );

        // Step 2: If a new file was provided, upload it via pre-signed URL
        if (file) {
            const presignedResponse = yield call(
                SteeringDataAPI.getUploadPresignedUrl,
                steeringDataGuid,
                file.name,
                ownerGuid
            );

            yield call(Request.uploadToS3, presignedResponse.url, file, "application/octet-stream");

            // Trigger backend file processing (text extraction + embedding)
            try {
                yield call(SteeringDataAPI.processFile, steeringDataGuid);
            } catch (processError) {
                // Non-fatal: file is uploaded, processing will occur on next deploy
                console.warn("process-file trigger failed (non-blocking):", processError);
            }
        }

        // Step 3: Dispatch success
        yield put(actions.updateSteeringDataSuccess(record));
        yield put(
            notificationActions.pushToasterMessage(
                "Steering data record updated successfully.",
                MSGTYPE.SUCCESS
            )
        );

        // Refresh the list
        yield put(actions.fetchSteeringDataList());
    } catch (error) {
        yield put(
            actions.updateSteeringDataFailed(error?.message || "Failed to update steering data")
        );
        yield put(notificationActions.apiCallError(error, action));
    }
}

// --- Delete ---

export function* deleteSteeringDataSaga(action) {
    const { payload } = action;
    try {
        yield put(actions.setSteeringDataLoading(true));

        yield call(SteeringDataAPI.deleteSteeringData, payload);

        yield put(actions.deleteSteeringDataSuccess(payload));
        yield put(
            notificationActions.pushToasterMessage(
                "Steering data record deleted successfully.",
                MSGTYPE.SUCCESS
            )
        );

        // Refresh the list
        yield put(actions.fetchSteeringDataList());
    } catch (error) {
        yield put(
            actions.deleteSteeringDataFailed(error?.message || "Failed to delete steering data")
        );
        yield put(notificationActions.apiCallError(error, action));
    }
}

// --- Children (hidden per-file rows of a url-files parent) ---

export function* fetchSteeringDataChildrenSaga(action) {
    const parentGuid = action.payload;
    try {
        const children = yield call(SteeringDataAPI.getSteeringDataChildren, parentGuid);
        yield put(actions.fetchSteeringDataChildrenSuccess({ parentGuid, children }));
    } catch (error) {
        yield put(
            actions.fetchSteeringDataChildrenFailed({
                parentGuid,
                error: error?.message || "Failed to fetch files",
            })
        );
        yield put(notificationActions.apiCallError(error, action));
    }
}

// When the list changes (e.g. a url crawl spawns/updates child files, which
// fires steeringDataListChanged → fetchSteeringDataList → success here), refresh
// any open Files sublists so newly-added files appear without a manual reopen.
export function* refreshLoadedChildrenSaga() {
    const parentGuids = yield select(getLoadedChildParentGuids);
    for (const parentGuid of parentGuids) {
        yield put(actions.fetchSteeringDataChildren(parentGuid));
    }
}

export function* deleteSteeringDataChildSaga(action) {
    const { parentGuid, childGuid } = action.payload;
    try {
        yield call(SteeringDataAPI.deleteSteeringDataChild, parentGuid, childGuid);
        yield put(actions.deleteSteeringDataChildSuccess({ parentGuid, childGuid }));
        yield put(
            notificationActions.pushToasterMessage("File deleted successfully.", MSGTYPE.SUCCESS)
        );
    } catch (error) {
        yield put(
            actions.deleteSteeringDataChildFailed({
                parentGuid,
                error: error?.message || "Failed to delete file",
            })
        );
        yield put(notificationActions.apiCallError(error, action));
    }
}

// --- Real-time status updates (IoT messaging) ---

// Subscribes to the "steeringDataStatus" event. Incoming model is
// { steeringDataGuid, embeddingStatus, chunkCount?, embeddingError? }; it is
// dispatched straight to updateSteeringDataStatus to update the row in place.
export function* subscribeSteeringDataStatus() {
    yield put(
        messagingActions.subscribe(0, {
            eventName: STEERING_DATA_STATUS_EVENT,
            action: (model) => actions.updateSteeringDataStatus(model),
        })
    );
}

// Subscribes to the "steeringDataListChanged" event. Fired when URL crawling
// spawns new child records (and removes the parent); refetches the list so the
// grid reflects the new rows without a manual refresh.
export function* subscribeSteeringDataListChanged() {
    yield put(
        messagingActions.subscribe(0, {
            eventName: STEERING_DATA_LIST_CHANGED_EVENT,
            action: () => actions.fetchSteeringDataList({ silent: true }),
        })
    );
}

// --- Root Saga ---

export function* steeringDataSagas() {
    yield all([
        subscribeSteeringDataStatus(),
        subscribeSteeringDataListChanged(),
        takeLatest(actions.fetchSteeringDataList, fetchSteeringDataListSaga),
        takeLatest(actions.fetchSteeringDataRecord, fetchSteeringDataRecordSaga),
        takeLatest(actions.createSteeringData, createSteeringDataSaga),
        takeLatest(actions.createSteeringDataBatch, createSteeringDataBatchSaga),
        takeLatest(actions.updateSteeringData, updateSteeringDataSaga),
        takeLatest(actions.deleteSteeringData, deleteSteeringDataSaga),
        takeEvery(actions.fetchSteeringDataChildren, fetchSteeringDataChildrenSaga),
        takeEvery(actions.deleteSteeringDataChild, deleteSteeringDataChildSaga),
        takeLatest(actions.fetchSteeringDataListSuccess, refreshLoadedChildrenSaga),
    ]);
}

export default steeringDataSagas;
